/**
 * Prediction Engine V2 — Component Model
 *
 * Parameter estimation from historical race data.
 * Estimates qualifying, race, overtake, bonus, and DNF parameters
 * for each driver and constructor.
 */

import type {
  TeamCalculatorConstructor,
  TeamCalculatorDriver,
} from "../algorithms/types";
import { getTrackClassification } from "../statistics/track-classification";
import type { TrackType } from "../statistics/types";
import { clamp } from "./distributions";
import {
  getConfidenceForDataVolume,
  getDefaultQualiPosition,
} from "./edge-cases";
import {
  applySeasonDiscount,
  computeWeights,
  type WeightedRace,
  weightedMean,
  weightedVariance,
} from "./recency-weights";
import type {
  ConstructorSimParams,
  DriverSimParams,
  PredictionRaceContext,
  RecencyConfig,
} from "./types";

// ============================================================================
// Track Overtaking Factors
// ============================================================================

const TRACK_OVERTAKING_FACTOR: Record<TrackType, number> = {
  street: 0.5,
  technical: 0.7,
  mixed: 1.0,
  high_speed: 1.3,
};

// ============================================================================
// Driver Parameter Estimation
// ============================================================================

export function estimateDriverParams(
  driver: TeamCalculatorDriver,
  raceContext: PredictionRaceContext,
  config: RecencyConfig,
  allDrivers?: TeamCalculatorDriver[],
): DriverSimParams {
  const history = driver.raceHistory;

  // Handle no-history edge case
  if (history.length === 0) {
    return buildNoHistoryParams(driver, raceContext, allDrivers);
  }

  // Reverse so index 0 = most recent
  const reversed = [...history].reverse();

  const rawWeights = computeWeights(
    reversed.length,
    config,
    driver.constructorSlug,
  );
  // Heavily discount previous-season races — current-season data is far more predictive.
  // This prevents e.g. a driver's 2025 history with a different team from dominating
  // predictions when they only have 1-2 current-season races available.
  const weights = applySeasonDiscount(rawWeights, reversed, raceContext.season);

  // Qualifying
  const { expectedQuali, qualiStdDev } = estimateQualiParams(
    reversed,
    weights,
    raceContext.trackType,
    raceContext.season,
  );

  // Race delta (positive = gains positions)
  const { avgDelta, deltaStdDev } = estimateRaceDeltaParams(
    reversed,
    weights,
    raceContext.season,
  );

  // Overtakes
  const expectedOvertakes = estimateOvertakeParams(
    reversed,
    weights,
    expectedQuali,
    raceContext.trackType,
  );

  // Binary probabilities
  const fastestLapProb = estimateRateParam(
    reversed.map((r) => r.fastestLap),
    weights,
    0.02,
    0.35,
  );
  const dotdProb = estimateRateParam(
    reversed.map((r) => r.driverOfTheDay),
    weights,
    0.03,
    0.25,
  );
  const dnfProb = estimateRateParam(
    reversed.map((r) => r.raceDnf),
    weights,
    0.02,
    0.25,
  );

  // Sprint parameters
  const sprintRaces = reversed.filter((r) => r.isSprintWeekend);
  const sprint = estimateSprintParams(
    sprintRaces,
    weights,
    expectedQuali,
    raceContext.trackType,
  );

  const confidence = getConfidenceForDataVolume(history.length);

  return {
    expectedQuali,
    qualiStdDev,
    avgQualiToRaceDelta: avgDelta,
    raceDeltaStdDev: deltaStdDev,
    expectedOvertakes,
    fastestLapProb,
    dotdProb,
    dnfProb,
    ...sprint,
    confidence,
  };
}

// ============================================================================
// Outlier Detection
// ============================================================================

/** Maximum qualifying standard deviation (prevents unrealistic simulation spread) */
const MAX_QUALI_STD_DEV = 2.5;

/** Maximum race delta standard deviation */
const MAX_DELTA_STD_DEV = 3.5;

/**
 * Maximum per-race delta allowed from cross-season data.
 * Extreme recovery drives (Q20→R8 = +12) in a previous season are almost always
 * anomalies (penalty, rain, red flag restart) that won't repeat under new regulations.
 * Capping at ±6 prevents these from inflating the weighted mean.
 */
const MAX_CROSS_SEASON_DELTA = 6;

/** How much of the season median to blend into the qualifying estimate */
const SEASON_ANCHOR_WEIGHT = 0.25;

/**
 * Detect outliers using the IQR method and reduce their weights.
 * Positions beyond Q1 - 1.5*IQR or Q3 + 1.5*IQR get weight reduced to 20%.
 * Works for both directions: bad driver great day, great driver bad day.
 */
function applyOutlierDetection(
  values: number[],
  weights: WeightedRace[],
): WeightedRace[] {
  if (values.length < 4) return weights; // Need enough data for IQR

  const sorted = [...values].sort((a, b) => a - b);
  const q1 = sorted[Math.floor(sorted.length * 0.25)];
  const q3 = sorted[Math.floor(sorted.length * 0.75)];
  const iqr = q3 - q1;
  const lowerFence = q1 - 1.5 * iqr;
  const upperFence = q3 + 1.5 * iqr;

  const adjusted = weights.map((w) => {
    if (w.index < values.length) {
      const isOutlier =
        values[w.index] < lowerFence || values[w.index] > upperFence;
      return { ...w, weight: isOutlier ? w.weight * 0.2 : w.weight };
    }
    return w;
  });

  // Re-normalize
  const total = adjusted.reduce((s, w) => s + w.weight, 0);
  if (total > 0) {
    for (const w of adjusted) {
      w.weight /= total;
    }
  }

  return adjusted;
}

/**
 * Compute the median of an array of numbers.
 */
function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
}

// ============================================================================
// Qualifying Parameters
// ============================================================================

function estimateQualiParams(
  history: typeof Array.prototype & { qualifyingPosition: number | null }[],
  weights: WeightedRace[],
  trackType: TrackType,
  currentSeason?: number,
): { expectedQuali: number; qualiStdDev: number } {
  // Extract qualifying positions, using race position as fallback
  const qualiPositions = (history as any[]).map(
    (r) => r.qualifyingPosition ?? r.racePosition ?? 15,
  );

  // Apply outlier detection — reduces weight of freak results (e.g., VER Q16 at Brazil)
  const outlierAdjustedWeights = applyOutlierDetection(qualiPositions, weights);

  const decayWeightedQuali = weightedMean(
    qualiPositions,
    outlierAdjustedWeights,
  );
  const qualiVariance = weightedVariance(
    qualiPositions,
    outlierAdjustedWeights,
  );

  // Season anchor: use current-season-only qualifying positions when available.
  // This prevents previous-season data (e.g. SAI 2025 Williams P7-9) from anchoring
  // the prediction at historically better positions than what the driver has shown in 2026.
  const currentSeasonPositions = currentSeason
    ? (history as any[])
        .filter((r) => r.season >= currentSeason)
        .map((r) => r.qualifyingPosition ?? r.racePosition ?? 15)
    : [];
  const anchorPositions =
    currentSeasonPositions.length >= 1
      ? currentSeasonPositions
      : qualiPositions;
  const seasonMedian = median(anchorPositions);

  // Blend: season anchor (25%) + decay-weighted estimate (75%)
  const blendedQuali =
    SEASON_ANCHOR_WEIGHT * seasonMedian +
    (1 - SEASON_ANCHOR_WEIGHT) * decayWeightedQuali;

  // Track-type adjustment: compare driver's performance on this track type vs overall.
  // Use the already-computed season-discounted weights (outlierAdjustedWeights) to
  // compute both averages, so 2025 mixed-track data doesn't dominate the adjustment
  // for 2026 predictions (where only 1-2 current-season races exist).
  const trackTypeIndices = (history as any[])
    .map((r, i) => ({
      i,
      isMatch: getTrackClassification(r.raceSlug) === trackType,
    }))
    .filter((x) => x.isMatch)
    .map((x) => x.i);

  let trackAdjustment = 0;
  if (trackTypeIndices.length >= 2) {
    // Build track-type weights from the discounted weights (same index mapping)
    const trackWeights = outlierAdjustedWeights.filter((w) =>
      trackTypeIndices.includes(w.index),
    );
    const trackTotal = trackWeights.reduce((s, w) => s + w.weight, 0);
    if (trackTotal > 0) {
      const normalizedTrackWeights = trackWeights.map((w) => ({
        ...w,
        weight: w.weight / trackTotal,
      }));
      const trackPositions = trackTypeIndices.map((i) => qualiPositions[i]);
      // Re-index for weightedMean (which uses w.index into the values array)
      const reIndexedTrackWeights = normalizedTrackWeights.map((w, idx) => ({
        index: idx,
        weight: w.weight,
      }));
      const trackAvg = weightedMean(trackPositions, reIndexedTrackWeights);
      const overallAvg = weightedMean(qualiPositions, outlierAdjustedWeights);
      trackAdjustment = trackAvg - overallAvg;
    }
  }

  const expectedQuali = clamp(blendedQuali + trackAdjustment, 1, 20);
  // Cap variance to prevent unrealistic simulation spread
  const qualiStdDev = Math.min(
    Math.max(Math.sqrt(qualiVariance), 1.5),
    MAX_QUALI_STD_DEV,
  );

  return { expectedQuali, qualiStdDev };
}

// ============================================================================
// Race Delta Parameters
// ============================================================================

function estimateRaceDeltaParams(
  history: any[],
  weights: WeightedRace[],
  currentSeason?: number,
): { avgDelta: number; deltaStdDev: number } {
  // Delta = qualifying - race (positive = gained positions)
  const deltas = history.map((r) => {
    if (r.raceDnf) return 0; // Exclude DNFs from delta calculation
    const qualiPos = r.qualifyingPosition ?? r.racePosition ?? 15;
    const racePos = r.racePosition ?? qualiPos;
    let delta = qualiPos - racePos;
    // Cap extreme cross-season deltas: a +12 recovery drive (Q20→R8) from
    // a previous season is almost always an anomaly (penalty, rain, red flag)
    // that won't repeat. Without this cap, a single chaotic 2025 race can
    // inflate the weighted mean for 2026 predictions.
    //
    // Fix C: Also cap deltas from races where qualifyingPosition == null
    // (pit-lane start/DQ) in ANY season — these recovery drives are anomalies
    // regardless of season, and inflate the delta mean.
    const isCrossSeason = currentSeason && r.season < currentSeason;
    const isPitLaneOrDq = r.qualifyingPosition == null;
    if (isCrossSeason || isPitLaneOrDq) {
      delta = Math.max(
        -MAX_CROSS_SEASON_DELTA,
        Math.min(MAX_CROSS_SEASON_DELTA, delta),
      );
    }
    return delta;
  });

  // Filter out DNF races from weights for delta calculation
  const nonDnfWeights = weights.filter(
    (w) => w.index < history.length && !history[w.index].raceDnf,
  );
  // Re-normalize
  const totalW = nonDnfWeights.reduce((s, w) => s + w.weight, 0);
  const normalizedWeights =
    totalW > 0
      ? nonDnfWeights.map((w) => ({
          ...w,
          weight: w.weight / totalW,
        }))
      : nonDnfWeights;

  // Apply outlier detection to race deltas (e.g., VER gaining 13 positions at Brazil)
  const outlierAdjustedWeights = applyOutlierDetection(
    deltas,
    normalizedWeights,
  );

  const avgDelta = weightedMean(deltas, outlierAdjustedWeights);
  const deltaVariance = weightedVariance(deltas, outlierAdjustedWeights);

  return {
    avgDelta,
    // Cap delta variance to prevent unrealistic spread
    deltaStdDev: Math.min(
      Math.max(Math.sqrt(deltaVariance), 1.0),
      MAX_DELTA_STD_DEV,
    ),
  };
}

// ============================================================================
// Overtake Parameters
// ============================================================================

function estimateOvertakeParams(
  history: any[],
  weights: WeightedRace[],
  expectedGridPos: number,
  trackType: TrackType,
): number {
  // Exclude pit-lane-start races from overtake baseline estimation entirely.
  // A driver starting from the back (null qualifying) generates artificially high
  // overtake counts. Even a 0.5 discount leaves the estimate inflated when that
  // race carries the bulk of the recency weight (e.g. the only current-season race).
  // Setting the contribution to 0 forces the estimate to rely on non-pit-lane history
  // and the grid-delta adjustment below instead.
  const overtakes = history.map((r) => {
    if (r.qualifyingPosition == null) return 0;
    return r.overtakes ?? 0;
  });
  const historicalAvg = weightedMean(overtakes, weights);

  // Track overtaking factor
  const trackFactor = TRACK_OVERTAKING_FACTOR[trackType];

  // historicalAvg already captures the driver's typical starting position and overtaking
  // tendency — do NOT multiply by gridFactor again (double-counts grid position effect).
  // Apply a small additive grid adjustment only when expected grid differs significantly
  // from historical average (e.g. a one-off penalty pushes them further back).
  const historicalGridAvg =
    weights.length > 0
      ? weightedMean(
          history.map((r) => r.qualifyingPosition ?? 15),
          weights,
        )
      : expectedGridPos;
  const gridDelta = Math.max(0, expectedGridPos - historicalGridAvg);
  const gridAdjustment = gridDelta * 0.3; // +0.3 expected overtakes per extra grid row back

  const expected = (historicalAvg + gridAdjustment) * trackFactor;

  // Fix B: Backmarker overtake ceiling — a genuinely slow car at P15+
  // can't do 5+ overtakes. They're fighting equally slow cars with no
  // meaningful pace advantage. Cap overtakes more aggressively for backmarkers.
  const globalCap = 7.0;
  const cap =
    expectedGridPos >= 15 ? Math.max(2.0, historicalAvg * 0.6) : globalCap;

  return Math.min(Math.max(expected, 0.5), cap);
}

// ============================================================================
// Rate Parameter (for boolean events)
// ============================================================================

function estimateRateParam(
  events: boolean[],
  weights: WeightedRace[],
  floor: number,
  cap: number,
): number {
  const values = events.map((e) => (e ? 1 : 0));
  const rate = weightedMean(values, weights);
  return clamp(rate, floor, cap);
}

// ============================================================================
// Sprint Parameters
// ============================================================================

function estimateSprintParams(
  sprintHistory: any[],
  _allWeights: WeightedRace[],
  expectedQuali: number,
  trackType: TrackType,
): {
  avgSprintQualiToRaceDelta: number;
  sprintDeltaStdDev: number;
  expectedSprintOvertakes: number;
  sprintFastestLapProb: number;
  sprintDnfProb: number;
} {
  if (sprintHistory.length === 0) {
    // Use main race parameters scaled for shorter sprint (~100km, ~30min)
    return {
      avgSprintQualiToRaceDelta: 0,
      sprintDeltaStdDev: 1.0,
      expectedSprintOvertakes: Math.max(
        0.2,
        (expectedQuali - 3) * 0.25 * TRACK_OVERTAKING_FACTOR[trackType],
      ),
      sprintFastestLapProb: 0.05,
      sprintDnfProb: 0.02,
    };
  }

  // Use sprint-specific weights (simple equal weighting for limited data)
  const sprintWeights: WeightedRace[] = sprintHistory.map((_, i) => ({
    index: i,
    weight: 1 / sprintHistory.length,
  }));

  const deltas = sprintHistory.map((r) => {
    if (r.sprintDnf) return 0;
    const startPos = r.qualifyingPosition ?? 10;
    const finishPos = r.sprintPosition ?? startPos;
    let delta = startPos - finishPos;
    // Cap sprint deltas at ±6 when qualifying was anomalous (penalty, rain, etc.).
    // HAM Q18→P2 (+16) or Q19→P6 (+13) are recovery drives from deep in the grid
    // that won't repeat when he qualifies P4. Without this cap, a front-runner's
    // sprint delta gets massively inflated by a few outlier weekends.
    // Use the same cap as race deltas (MAX_CROSS_SEASON_DELTA = 6).
    if (Math.abs(startPos - expectedQuali) > 5) {
      delta = Math.max(-6, Math.min(6, delta));
    }
    return delta;
  });

  const avgDelta = weightedMean(deltas, sprintWeights);
  const deltaVar = weightedVariance(deltas, sprintWeights);

  // Cap sprint overtakes from anomalous qualifying positions (same logic as deltas)
  const overtakes = sprintHistory.map((r) => {
    const ot = r.sprintOvertakes ?? 0;
    const startPos = r.qualifyingPosition ?? 10;
    if (Math.abs(startPos - expectedQuali) > 5) {
      return Math.min(ot, 3); // Cap at 3 OT for anomalous weekends
    }
    return ot;
  });
  const avgOvertakes = weightedMean(overtakes, sprintWeights);

  const flRate = estimateRateParam(
    sprintHistory.map((r) => r.sprintFastestLap),
    sprintWeights,
    0.02,
    0.3,
  );

  const dnfRate = estimateRateParam(
    sprintHistory.map((r) => r.sprintDnf),
    sprintWeights,
    0.01,
    0.15,
  );

  return {
    avgSprintQualiToRaceDelta: avgDelta,
    sprintDeltaStdDev: Math.max(Math.sqrt(deltaVar), 1.0),
    expectedSprintOvertakes: Math.max(avgOvertakes, 0.3),
    sprintFastestLapProb: flRate,
    sprintDnfProb: dnfRate,
  };
}

// ============================================================================
// No-History Fallback
// ============================================================================

function buildNoHistoryParams(
  driver: TeamCalculatorDriver,
  _raceContext: PredictionRaceContext,
  allDrivers?: TeamCalculatorDriver[],
): DriverSimParams {
  const expectedQuali = allDrivers
    ? getDefaultQualiPosition(
        driver.constructorSlug,
        allDrivers,
        driver.driverSlug,
      )
    : 12;

  return {
    expectedQuali,
    qualiStdDev: 4.0, // Wide uncertainty
    avgQualiToRaceDelta: 0,
    raceDeltaStdDev: 3.0,
    expectedOvertakes: Math.max(0.5, (expectedQuali - 3) * 0.5),
    fastestLapProb: 0.05,
    dotdProb: 0.05,
    dnfProb: 0.05,
    avgSprintQualiToRaceDelta: 0,
    sprintDeltaStdDev: 1.5,
    expectedSprintOvertakes: Math.max(0.2, (expectedQuali - 3) * 0.2),
    sprintFastestLapProb: 0.05,
    sprintDnfProb: 0.02,
    confidence: 0.2,
  };
}

// ============================================================================
// Constructor Parameter Estimation
// ============================================================================

export function estimateConstructorParams(
  ctor: TeamCalculatorConstructor,
  config: RecencyConfig,
): ConstructorSimParams {
  const history = ctor.raceHistory;

  if (history.length === 0) {
    return {
      expectedPitStopPoints: 5,
      pitStopStdDev: 3,
      fastestLapProb: 0.1,
      sprintFastestLapProb: 0.1,
      confidence: 0.2,
    };
  }

  const reversed = [...history].reverse();
  const weights = computeWeights(reversed.length, config);

  const pitStopPts = reversed.map((r) => r.pitStopTimePoints ?? 0);
  const expectedPitStopPoints = weightedMean(pitStopPts, weights);
  const pitStopVar = weightedVariance(pitStopPts, weights);

  const flRate = estimateRateParam(
    reversed.map((r) => r.fastestLap),
    weights,
    0.02,
    0.4,
  );

  const sprintFlRate = estimateRateParam(
    reversed.filter((r) => r.isSprintWeekend).map((r) => r.sprintFastestLap),
    reversed
      .filter((r) => r.isSprintWeekend)
      .map((_, i) => ({
        index: i,
        weight:
          1 / Math.max(reversed.filter((r) => r.isSprintWeekend).length, 1),
      })),
    0.02,
    0.35,
  );

  return {
    expectedPitStopPoints,
    pitStopStdDev: Math.max(Math.sqrt(pitStopVar), 1.0),
    fastestLapProb: flRate,
    sprintFastestLapProb: sprintFlRate,
    confidence: getConfidenceForDataVolume(history.length),
  };
}
